// Modeled on frontend/src/auth/AuthContext.tsx — me() on mount, global 401
// handler, feature helper instead of the local role helper.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../api/api";
import { setUnauthorizedHandler } from "../api/client";
import type { Me } from "../api/types";

interface AuthValue {
  user: Me | null;
  loading: boolean;
  setUser: (u: Me | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  hasFeature: (feature: string) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();

  // Cached server data belongs to ONE account. Signing in as someone else in
  // the same tab (after a logout or an expired session) must not show the
  // previous account's sites, members or invoices for even one render — so
  // the cache is dropped BEFORE the new user is committed.
  const lastUserId = useRef<number | null>(null);
  const setUser = useCallback(
    (u: Me | null) => {
      if (u && lastUserId.current !== null && lastUserId.current !== u.id) queryClient.clear();
      if (u) lastUserId.current = u.id;
      setUserState(u);
    },
    [queryClient],
  );

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
    } catch {
      setUser(null);
    }
  }, [setUser]);

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    refresh().finally(() => setLoading(false));
    return () => setUnauthorizedHandler(null);
  }, [refresh, setUser]);

  // Keep the UI language in sync with the account preference.
  useEffect(() => {
    if (user?.lang && i18n.language !== user.lang) {
      void i18n.changeLanguage(user.lang);
    }
  }, [user?.lang, i18n]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // An already-expired session 401s here — the local state reset below
      // is the part that matters, so don't surface (or leak) the rejection.
    } finally {
      setUser(null);
      // Drop every cached response (sites, fleet, subscription, device
      // pages' data) so nothing of this account outlives the session.
      queryClient.clear();
    }
  }, [queryClient, setUser]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      setUser,
      refresh,
      logout,
      hasFeature: (feature: string) => !!user && user.features.includes(feature),
    }),
    [user, loading, setUser, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
